/**
 * W Insurance Data Layer Initialization
 * Created: February 2026
 * 
 * Initializes the global wawa_data_layer object for Adobe Analytics/Tealium tracking.
 * Matches INS-Renters OLB Tagging Requirements v1.0.1 Variable Map.
 */
/* eslint-disable no-use-before-define */

/**
 * Detect application flow based on URL and session
 * @returns {string} 'new', 'retrieve:abq', or 'retrieve:iqq'
 * 
 * Flow definitions:
 * - "new" - new quote
 * - "retrieve:abq" - retrieve quote at ABQ: user returns to Contact – Details Your Home page (& any page thereafter), but not Coverage – Quote page
 * - "retrieve:iqq" - retrieve quote at IQQ: user returns to Coverage – Quote page
 */
export const detectApplicationFlow = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const pathname = window.location.pathname;
  
  // Check for retrieval indicators
  const isRetrieve = pathname.includes('/retrieve-quote') || 
                    pathname.includes('/quick-retrieve') ||
                    urlParams.has('quoteId') || 
                    urlParams.has('quoteNumber') ||
                    sessionStorage.getItem('isRetrievedQuote') === 'true';
  
  if (isRetrieve) {
    // Determine if user is at Coverage - Quote page (IQQ) or earlier pages (ABQ)
    const isQuotePage = pathname.includes('/quote') || 
                       pathname.includes('/coverage') ||
                       pathname.includes('/quote-summary') ||
                       sessionStorage.getItem('retrieveFlowType') === 'iqq';
    
    if (isQuotePage) {
      return 'retrieve:iqq';  // IQQ: retrieve quote at Coverage - Quote page
    } else {
      return 'retrieve:abq';  // ABQ: retrieve quote at Contact – Details Your Home page
    }
  }
  
  // Default to new quote
  return 'new';
};

/**
 * Helper function to detect authentication status
 * @returns {string} 'logged in' or 'logged out'
 */
const getAuthenticationStatus = () => {
  // Check for session token
  const sessionToken = sessionStorage.getItem('X_GWRE_SESSION');
  
  // Check for auth token in localStorage
  const authToken = localStorage.getItem('authToken') || 
                   localStorage.getItem('jwt') ||
                   localStorage.getItem('accessToken');
  
  // Check for user data
  const userData = sessionStorage.getItem('userData') || 
                  localStorage.getItem('userData');
  
  // If any authentication indicator exists, user is logged in
  if (sessionToken || authToken || userData) {
    return 'logged in';
  }
  
  return 'logged out';
};

/**
 * Helper function to detect platform
 * @returns {string} 'Mobile' or 'Desktop'
 */
const getPlatform = () => {
  const userAgent = navigator.userAgent || '';
  const isMobile = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  return isMobile ? 'Mobile' : 'Desktop';
};

/**
 * Helper function to detect environment
 * @returns {string} 'dev', 'qa', or 'prod'
 */
const getEnvironment = () => {
  const hostname = window.location.hostname;
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'dev';
  } else if (hostname.includes('renters.wawanesa.com')) {
    return 'prod';
  } else {
    return 'qa';
  }
};

/**
 * Update data layer with new values
 * Merges updates into existing winsuranceMediaTagvalues object
 * @param {Object} updates - Object containing properties to update
 */
export const updateDataLayer = (updates) => {
  if (!window.wins_data_layer) {
    initializeDataLayer();
  }

  // Deep merge helper function
  function deepMerge(target, source) {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          target[key] = target[key] || {};
          deepMerge(target[key], source[key]);
        } else {
          target[key] = source[key];
        }
      }
    }
  }

  deepMerge(window.wins_data_layer, updates);
  
};

/**
 * Track CTA (Call-To-Action) clicks with proper formatting
 * Per INS document pages 16-38, CTA format: {channel}:{subchannel1}:{subchannel2}:{page}-{action}
 * 
 * @param {string} action - The action performed (e.g., 'email quote', 'print quote', 'download receipt')
 * @param {string} pageName - Optional override for page name (uses current page from data layer if not provided)
 * @returns {string} Formatted CTA string
 * 
 * Examples from INS document:
 * - trackCTA('email quote', 'coverage quote') → "insurance:renters:quote:coverage quote-email quote"
 * - trackCTA('print quote', 'coverage quote') → "insurance:renters:quote:coverage quote-print quote"
 * - trackCTA('download receipt', 'confirmation modal') → "insurance:renters:quote:confirmation modal-download receipt"
 */
export const trackCTA = (action, pageName = null) => {
  if (!window.wins_data_layer) {
    initializeDataLayer();
  }

  const dl = window.wins_data_layer;
  const page = pageName || dl.page || '';
  
  // Format: channel:subchannel1:subchannel2:page-action
  // Example: "insurance:renters:quote:coverage quote-email quote"
  const ctaString = `${dl.channel}:${dl.subchannel1}:${dl.subchannel2}:${page}-${action}`;
  
  updateDataLayer({ cta: ctaString });
  
  console.log('[Analytics] CTA Tracked:', ctaString);
  return ctaString;
};

/**
 * Set error message with proper formatting
 * Per INS document pages 39-47, error format: {page}-{error_reason}
 *
 * Accepts either:
 *   - A single errorReason string (with optional pageName override), OR
 *   - An array of {errorReason, pageName} pairs for multi-KO scenarios,
 *     which are formatted as a comma-delimited string:
 *     "insurance:renters:quote:sorry qnb-dog breed not eligible, insurance:renters:quote:sorry qnb-clue report down"
 *
 * @param {string|Array<{errorReason: string, pageName: string}>} errorReasonOrArray
 * @param {string} [pageName] - Optional page name override (single-reason path only)
 * @returns {string} Formatted error string
 *
 * Examples from INS document:
 * - setError('out of market', 'sorry outofareamodal') → "sorry outofareamodal-out of market"
 * - setError('session timeout', 'sorry sessionexpired') → "sorry sessionexpired-session timeout"
 * - setError('dog breed not eligible', 'sorry qnb') → "sorry qnb-dog breed not eligible"
 * - setError('create quote', 'sorry technicalerror') → "sorry technicalerror-create quote"
 * - setError([{errorReason:'dog breed not eligible',pageName:'sorry qnb'},{errorReason:'clue report down',pageName:'sorry qnb'}])
 *     → "insurance:renters:quote:sorry qnb-dog breed not eligible, insurance:renters:quote:sorry qnb-clue report down"
 */
export const setError = (errorReasonOrArray, pageName = null) => {
  if (!window.wins_data_layer) {
    initializeDataLayer();
  }

  // Multi-reason: array of {errorReason, pageName} pairs → comma-delimited string
  if (Array.isArray(errorReasonOrArray)) {
    const parts = errorReasonOrArray.map(({ errorReason: reason, pageName: pn }) =>
      `insurance:renters:quote:${pn}-${reason}`
    );
    const errorString = parts.join(', ');
    updateDataLayer({ error: errorString });
    return errorString;
  }

  const dl = window.wins_data_layer;
  const page = pageName || dl.page || '';

  // Format: {page}-{error_reason}
  const errorString = `${page}-${errorReasonOrArray}`;

  updateDataLayer({ error: errorString });

  return errorString;
};

/**
 * Clear CTA and error values (use after tracking event is complete)
 */
export const clearInteractionTracking = () => {
  updateDataLayer({ 
    cta: '', 
    error: '' 
  });
};

/**
 * Pre-defined CTA actions from INS document for easy reference
 */
export const CTA_ACTIONS = {
  EMAIL_QUOTE: 'email quote',
  PRINT_QUOTE: 'print quote',
  DOWNLOAD_RECEIPT: 'download receipt',
  PRINTABLE_VERSION: 'printable version',
  START_CALL_AGENT: 'start-call an agent',
};

/**
 * Pre-defined error reasons from INS document (pages 39-47) for easy reference
 */
export const ERROR_REASONS = {
  // Out of Area (page 39)
  OUT_OF_MARKET: 'out of market',
  
  // Call Us To Proceed (page 40)
  QUOTE_CANNOT_BE_EDITED: 'quote cannot be edited online',
  
  // KO Reasons (page 41-42)
  RESIDENCE_TYPE_NOT_ELIGIBLE: 'residence type not apartment or single family home',
  MORE_THAN_2_NON_FAMILY_ADULTS: 'more than 2 non-family adults',
  RESIDENCE_TYPE_AND_ADULTS: 'residence type not apartment or single family home and more than 2 non-family adults',
  RESIDENCE_AND_HOME_FEATURES: 'residence type not insured and more than 2 non-family adults and home features and use combination',
  APARTMENT_HOME_FEATURES: 'residence type apartment and home features and use options',
  SINGLE_FAMILY_HOME_FEATURES: 'residence type single family home and home features and use options',
  
  // QNB Reasons (page 44-45)
  UNDERWRITING_GUIDELINES: 'underwriting guidelines',  // ✅ ADDED: Generic UW decline fallback
  PROPERTY_CLAIMS_PAST_5_YEARS: 'property insurance claims or losses in past 5 years',
  REGULAR_EMPLOYEES_AT_RESIDENCE: 'regular employees working at residence',
  WOOD_BURNING_STOVE: 'residence equipped with wood burning stove',
  DOG_BREED_NOT_ELIGIBLE: 'dog breed not eligible',
  BUSINESS_AND_PERMITTED_OCCUPANCIES: 'business conducted and permitted incidental occupancies residence premises',
  BUSINESS_AND_CLERICAL: 'business conducted and business pursuits-clerical',
  BUSINESS_AND_SALES: 'business conducted and business pursuits-sales, collector/messenger, installation/servicing',
  BUSINESS_AND_TEACHER: 'business conducted and business pursuits-teacher',
  CLUE_REPORT_DOWN: 'clue report down',
  
  // Maintenance (page 43)
  SYSTEM_OUTAGE: 'system outage',
  
  // Session Expired (page 46)
  SESSION_TIMEOUT: 'session timeout',
  
  // Technical Errors (page 47)
  TECHNICAL_ERROR: 'technical error',  // ✅ ADDED: Generic technical error
  CREATE_QUOTE: 'create quote',
  UPDATE_QUOTE: 'update quote',
  RETRIEVE_QUOTE: 'retrieve quote',
};

/**
 * Initialize the global data layer matching Variable Map from Tagging Requirements
 * This should be called once at application startup
 */
export const initializeDataLayer = () => {
  // Initialize global data layer matching INS-Renters OLB Tagging Requirements v1.0.1
  window.wins_data_layer = {
    // Required per Variable Map table
    category: ['insurance quote'],               
    channel: 'insurance',                        
    subchannel1: 'renters',                     
    subchannel2: 'quote',                        // Per INS doc: all pages use 'quote' for renters quote flow
    page: '',                                   
    product: ['renters'],                        
    quantity: ['1'],                             
    
    // User/Session
    IsAuthenticated: getAuthenticationStatus(), 
    sessionid: sessionStorage.getItem('X_GWRE_SESSION') || '', 
    
    // Quote/Policy information
    ins_quoteid: '',                             
    ins_quote_id: '',                            
    state: '',                                   
    
    // Application flow (dynamically detected)
    flow: detectApplicationFlow(),              
    
    // Interaction tracking
    cta: '',                                     
    error: '',                                   
    
    // Technical
   platform: getPlatform(),                     
    
    // Additional (legacy/compatibility)
    coverage_type: 'renters',
    premium: '',
    product_type: 'Homeowners'
  };

  
  // Expose updateDataLayer globally for AdobeAnalytics component
  window.updateDataLayer = updateDataLayer;
  
  // Expose CTA and error tracking functions globally
  window.trackCTA = trackCTA;
  window.setError = setError;
  window.clearInteractionTracking = clearInteractionTracking;
  window.CTA_ACTIONS = CTA_ACTIONS;
  window.ERROR_REASONS = ERROR_REASONS;
};

export default {
  initializeDataLayer,
  updateDataLayer,
  detectApplicationFlow,
  trackCTA,
  setError,
  clearInteractionTracking,
  CTA_ACTIONS,
  ERROR_REASONS
};
